import { create } from 'zustand';

type DrawerView = 'closed' | 'tooltip' | 'drawer' | 'ticket';

interface SearchResult {
  type: 'faq' | 'article' | 'video';
  id: string;
  title: string;
  snippet: string;
  relevanceScore: number;
  categoryId: string;
  categoryTitle: string;
}

interface DrawerState {
  selectedCategory: string | null;
  selectedFaqId: string | null;
  searchQuery: string;
  searchResults: SearchResult[];
}

interface SupportDrawerState {
  // Core state
  currentView: DrawerView;
  isDrawerOpen: boolean;
  isTicketModalOpen: boolean;

  // Drawer state
  drawerState: DrawerState;

  // Actions
  openTooltip: () => void;
  closeTooltip: () => void;
  toggleTooltip: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  openTicketModal: () => void;
  closeTicketModal: () => void;
  closeAll: () => void;

  // Drawer actions
  setSearchQuery: (query: string) => void;
  setSearchResults: (results: SearchResult[]) => void;
  setSelectedCategory: (categoryId: string | null) => void;
  selectCategory: (categoryId: string | null) => void;
  selectFaq: (faqId: string | null) => void;
  clearSearch: () => void;
  resetDrawerState: () => void;
}

const initialDrawerState: DrawerState = {
  selectedCategory: null,
  selectedFaqId: null,
  searchQuery: '',
  searchResults: [],
};

export const useSupportDrawer = create<SupportDrawerState>((set, get) => ({
  currentView: 'closed',
  isDrawerOpen: false,
  isTicketModalOpen: false,
  drawerState: initialDrawerState,

  openTooltip: () => set({ currentView: 'tooltip' }),
  closeTooltip: () => set({ currentView: 'closed' }),
  toggleTooltip: () => {
    const { currentView } = get();
    if (currentView === 'tooltip') {
      set({ currentView: 'closed' });
    } else if (currentView === 'closed') {
      set({ currentView: 'tooltip' });
    }
  },

  openDrawer: () =>
    set({
      currentView: 'drawer',
      isDrawerOpen: true,
    }),
  closeDrawer: () =>
    set({
      currentView: 'closed',
      isDrawerOpen: false,
      drawerState: initialDrawerState,
    }),

  openTicketModal: () =>
    set({
      currentView: 'ticket',
      isTicketModalOpen: true,
    }),
  closeTicketModal: () =>
    set({
      currentView: 'closed',
      isTicketModalOpen: false,
    }),

  closeAll: () =>
    set({
      currentView: 'closed',
      isDrawerOpen: false,
      isTicketModalOpen: false,
      drawerState: initialDrawerState,
    }),

  setSearchQuery: (query) =>
    set((state) => ({
      drawerState: { ...state.drawerState, searchQuery: query },
    })),
  setSearchResults: (results) =>
    set((state) => ({
      drawerState: { ...state.drawerState, searchResults: results },
    })),
  selectCategory: (categoryId) =>
    set((state) => ({
      drawerState: { ...state.drawerState, selectedCategory: categoryId, selectedFaqId: null },
    })),
  setSelectedCategory: (categoryId) =>
    set((state) => ({
      drawerState: { ...state.drawerState, selectedCategory: categoryId, selectedFaqId: null },
    })),
  selectFaq: (faqId) =>
    set((state) => ({
      drawerState: { ...state.drawerState, selectedFaqId: faqId },
    })),
  clearSearch: () =>
    set((state) => ({
      drawerState: { ...state.drawerState, searchQuery: '', searchResults: [] },
    })),
  resetDrawerState: () => set({ drawerState: initialDrawerState }),
}));

export type { SearchResult, DrawerState, SupportDrawerState };
